import { Component } from "react";
import { ModuleErrorFallback } from "./ModuleErrorFallback";
import { isChunkLoadError } from "../../../helper/lazyWithRetry";

/**
 * ModuleErrorBoundary
 *
 * Level 2 — Per lazy-loaded route isolation.
 * Placed inside SuspensedView in PrivateRoutes.jsx / PrivateRoutes2.jsx.
 * Catches errors in any single module (Shipment, Inventory, Transfer, etc.)
 * without affecting the rest of the app (sidebar, navbar, other modules).
 *
 * Props:
 *   - children   : The module component to protect
 *   - moduleName : Display name shown in the fallback card (e.g. "Shipment")
 *
 * Usage:
 *   <ModuleErrorBoundary moduleName="Shipment">
 *     <ShipmentPage />
 *   </ModuleErrorBoundary>
 */
class ModuleErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
    };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    // Ideal hook for external error reporting (e.g. Sentry).
    // Example: Sentry.captureException(error, { extra: { module: this.props.moduleName, ...errorInfo } });
    console.error(
      `[ModuleErrorBoundary] Error in module "${this.props.moduleName || "Unknown"}":`,
      error,
    );
    console.error("[ModuleErrorBoundary] Component stack:", errorInfo.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null, isChunkError: false });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ModuleErrorFallback
          moduleName={this.props.moduleName}
          error={this.state.error}
          isChunkError={this.state.isChunkError}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export { ModuleErrorBoundary };
